import axios from 'axios';
import { Dispatch, useEffect } from 'react';
import { Setting, Subscription } from '../App';
import { CUSTOMER_API_URL } from '../constants';
import { ProductInfoType, createProductInfo } from '../types/product-info-type';
import { createSummary, SummaryType } from '../types/summary-type';
import { DeliveryDateType } from '../types/delivery-date-type';
import { ProductResponseType } from '../types/product-response-type';
import { RecommendProductInfoType } from '../types/recommend-product-info-type';
import { RecommendProductResponseType } from '../types/recommend-product-response-type';

export const useSubscriptionDetail = (
  setting: Setting,
  setSubscriptionContractId: Dispatch<string>,
  setRegisteredProducts: Dispatch<ProductInfoType[]>,
  setSummary: Dispatch<SummaryType>,
  setDeliveryDate: Dispatch<DeliveryDateType>,
  setPausedProducts: Dispatch<ProductInfoType[]>,
  setRecommendProducts: Dispatch<RecommendProductInfoType[]>
) => {
  useEffect(() => {
    (async () => {
      try {
        const response = await axios.post(
          `${CUSTOMER_API_URL}?action=getSubscriptions`,
          {},
          { headers: { Authorization: setting.token } }
        );

        const subscriptions = response.data.filter((subscription: Subscription) => subscription.status === 'ACTIVE');
        if (subscriptions.length === 0) return;

        const subscription = subscriptions[0] as Subscription;
        setSubscriptionContractId(subscription.subscriptionContractId);

        const products = subscription.subscriptionLines.map((subscriptionLine) => {
          return createProductInfo(subscriptionLine);
        });
        setRegisteredProducts(products);

        const summary = createSummary(subscription);
        setSummary(summary);

        setDeliveryDate({
          deliveryDate: summary.deliveryDate,
          disableWeek: [
            subscription.shop.disableDeliveryDaysSun,
            subscription.shop.disableDeliveryDaysMon,
            subscription.shop.disableDeliveryDaysTue,
            subscription.shop.disableDeliveryDaysWed,
            subscription.shop.disableDeliveryDaysThr,
            subscription.shop.disableDeliveryDaysFri,
            subscription.shop.disableDeliveryDaysSat,
          ],
        });

        const purchasedProductsResponse = await axios.post(
          `${CUSTOMER_API_URL}?action=getPurchasedProducts`,
          {},
          { headers: { Authorization: setting.token } }
        );

        const pausedProducts = purchasedProductsResponse.data.reduce(
          (result: ProductInfoType[], purchasedProduct: ProductResponseType) => {
            // Mammababyは商品に対して、プランが1つのみなので、sellingPlanIdを考慮せず、variantIdのみで判定する
            if (products.every((product) => product.variantId !== purchasedProduct.variantId)) {
              result.push({
                productTitle: purchasedProduct.productTitle,
                image: purchasedProduct.image,
                variantTitle: purchasedProduct.variantTitle,
                originalPrice: purchasedProduct.originalPrice,
                price: purchasedProduct.price,
                quantity: 0,
                variantId: purchasedProduct.variantId,
                planId: purchasedProduct.planId,
              });
            }

            return result;
          },
          []
        );

        setPausedProducts(pausedProducts);

        const recommendProductsResponse = await axios.post(
          `${CUSTOMER_API_URL}?action=getRecommendProductVariants`,
          {},
          { headers: { Authorization: setting.token } }
        );

        const recommendProducts = recommendProductsResponse.data.reduce(
          (result: RecommendProductInfoType[], recommendProduct: RecommendProductResponseType) => {
            // Mammababyは商品に対して、プランが1つのみなので、sellingPlanIdを考慮せず、variantIdのみで判定する
            if (products.every((product) => product.variantId !== recommendProduct.variantId)) {
              result.push({
                productTitle: recommendProduct.productTitle,
                image: recommendProduct.image,
                variantTitle: recommendProduct.variantTitle,
                originalPrice: recommendProduct.originalPrice,
                price: recommendProduct.price,
                quantity: 0,
                variantId: recommendProduct.variantId,
                planId: recommendProduct.planId,
                message: recommendProduct.message,
              });
            }

            return result;
          },
          []
        );

        console.log(recommendProducts)

        setRecommendProducts(recommendProducts);
      } catch (e) {
        console.log(e);
      }
    })();
  }, [
    setDeliveryDate,
    setPausedProducts,
    setRecommendProducts,
    setRegisteredProducts,
    setSubscriptionContractId,
    setSummary,
    setting.token,
  ]);

  const getPausedProducts = (
    products: ProductInfoType[],
    success: (infos: ProductInfoType[]) => void,
    failed?: (message: string) => void
  ) => {
    (async () => {
      try {
        const purchasedProductsResponse = await axios.post(
          `${CUSTOMER_API_URL}?action=getPurchasedProducts`,
          {},
          { headers: { Authorization: setting.token } }
        );

        const pausedProducts = purchasedProductsResponse.data.reduce(
          (result: ProductInfoType[], purchasedProduct: ProductResponseType) => {
            // Mammababyは商品に対して、プランが1つのみなので、sellingPlanIdを考慮せず、variantIdのみで判定する
            if (products.every((product) => product.variantId !== purchasedProduct.variantId)) {
              result.push({
                productTitle: purchasedProduct.productTitle,
                image: purchasedProduct.image,
                variantTitle: purchasedProduct.variantTitle,
                originalPrice: purchasedProduct.originalPrice,
                price: purchasedProduct.price,
                quantity: 0,
                variantId: purchasedProduct.variantId,
                planId: purchasedProduct.planId,
              });
            }

            return result;
          },
          []
        );

        success(pausedProducts);
      } catch (e) {
        if (failed) {
          failed(e.message);
        }
      }
    })();
  };

  const getRegisteredProducts = (success: (infos: ProductInfoType[]) => void, failed?: (message: string) => void) => {
    (async () => {
      try {
        const response = await axios.post(
          `${CUSTOMER_API_URL}?action=getSubscriptions`,
          {},
          { headers: { Authorization: setting.token } }
        );

        const subscriptions = response.data.filter((subscription: Subscription) => subscription.status === 'ACTIVE');
        if (subscriptions.length === 0) return;

        const subscription = subscriptions[0] as Subscription;
        setSubscriptionContractId(subscription.subscriptionContractId);

        const products = subscription.subscriptionLines.map((subscriptionLine) => {
          return createProductInfo(subscriptionLine);
        });

        success(products);
      } catch (e) {
        if (failed) {
          failed(e.message);
        }
      }
    })();
  };

  const getRecommendProducts = (
    products: ProductInfoType[],
    success: (infos: RecommendProductInfoType[]) => void,
    failed?: (message: string) => void
  ) => {
    (async () => {
      try {
        const recommendProductsResponse = await axios.post(
          `${CUSTOMER_API_URL}?action=getRecommendProductVariants`,
          {},
          { headers: { Authorization: setting.token } }
        );

        const recommendProducts = recommendProductsResponse.data.reduce(
          (result: RecommendProductInfoType[], recommendProduct: RecommendProductResponseType) => {
            // Mammababyは商品に対して、プランが1つのみなので、sellingPlanIdを考慮せず、variantIdのみで判定する
            if (products.every((product) => product.variantId !== recommendProduct.variantId)) {
              result.push({
                productTitle: recommendProduct.productTitle,
                image: recommendProduct.image,
                variantTitle: recommendProduct.variantTitle,
                originalPrice: recommendProduct.originalPrice,
                price: recommendProduct.price,
                quantity: 0,
                variantId: recommendProduct.variantId,
                planId: recommendProduct.planId,
                message: recommendProduct.message,
              });
            }

            return result;
          },
          []
        );

        success(recommendProducts);
      } catch (e) {
        if (failed) {
          failed(e.message);
        }
      }
    })();
  };

  return {
    getRegisteredProducts,
    getPausedProducts,
    getRecommendProducts,
  };
};
